import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Form, Row, Table, Badge, Modal, Tab, Tabs } from 'react-bootstrap';

import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { NioButton, NioCard, NioSection } from '../../../components';
import api from '../../../utils/api';
import { useTranslation } from '../../../i18n/I18nProvider';

const SAV_TYPE_VALUES = [
  'reclamation',
  'remboursement',
  'retour-produit',
  'garantie',
  'assistance-technique',
  'suivi-intervention',
  'support-client',
  'evaluation-prestataire',
  'signalement-probleme',
  'historique',
];

const STATUS_VARIANTS = {
  pending: 'warning',
  'in-progress': 'info',
  resolved: 'success',
};

const PRIORITY_VARIANTS = {
  low: 'secondary',
  medium: 'primary',
  high: 'danger',
};

const DATE_LOCALES = { fr: 'fr-FR', en: 'en-US' };

function getStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Convertit un File en data URL (base64) pour l'envoi via JSON.
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(file);
  });
}

export default function SupportSav() {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const dateLocale = DATE_LOCALES[language] || 'fr-FR';

  const formatDate = useCallback((value) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleString(dateLocale);
    } catch {
      return '-';
    }
  }, [dateLocale]);

  const typeLabel = useCallback((type) => {
    if (!type) return '-';
    const key = `supportSav.types.${type}`;
    const label = t(key);
    return label !== key ? label : type;
  }, [t]);

  const statusInfo = useCallback((status) => ({
    label: t(`supportSav.statuses.${status}`) || status,
    variant: STATUS_VARIANTS[status] || STATUS_VARIANTS.pending,
  }), [t]);

  const priorityInfo = useCallback((priority) => ({
    label: t(`supportSav.priorities.${priority}`) || priority,
    variant: PRIORITY_VARIANTS[priority] || PRIORITY_VARIANTS.medium,
  }), [t]);
  const [user, setUser] = useState(getStoredUser);

  const [tab, setTab] = useState('new');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state - nouveau ticket
  const [form, setForm] = useState({
    type: 'reclamation',
    subject: '',
    description: '',
    requestId: '',
    requestTitle: '',
    providerName: '',
    priority: 'medium',
    rating: '',
    notifyEmail: true,
    notifySMS: false,
  });
  const [attachments, setAttachments] = useState([]); // [{ name, dataUrl }]
  const fileInputRef = useRef(null);

  // Filtre liste
  const [filterStatus, setFilterStatus] = useState('all');

  // Detail ticket (modal)
  const [openTicket, setOpenTicket] = useState(null);
  const [messageDraft, setMessageDraft] = useState('');
  const [messageAttachments, setMessageAttachments] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  const msgFileRef = useRef(null);

  useEffect(() => {
    const u = getStoredUser();
    if (!u) {
      navigate('/auth/login', { replace: true, state: { from: '/support-sav' } });
      return;
    }
    // L'admin n'a pas vocation a ouvrir des tickets : il les gere depuis son dashboard.
    if (String(u.role || '').trim().toLowerCase() === 'admin') {
      navigate('/admin/dashboard', { replace: true });
      return;
    }
    setUser(u);
  }, [navigate]);

  const fetchTickets = async () => {
    if (!user?.email) return;
    setLoading(true);
    try {
      const res = await api.getMySupportTickets(user.email);
      if (res?.success) setTickets(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e.message || t('supportSav.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.email) fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const filteredTickets = useMemo(() => {
    if (filterStatus === 'all') return tickets;
    return tickets.filter((t) => t.status === filterStatus);
  }, [tickets, filterStatus]);

  const stats = useMemo(() => {
    const pending = tickets.filter((t) => t.status === 'pending').length;
    const inProgress = tickets.filter((t) => t.status === 'in-progress').length;
    const resolved = tickets.filter((t) => t.status === 'resolved').length;
    return { total: tickets.length, pending, inProgress, resolved };
  }, [tickets]);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const remaining = Math.max(0, 5 - attachments.length);
      const next = await Promise.all(
        files.slice(0, remaining).map(async (f) => ({ name: f.name, dataUrl: await fileToDataUrl(f) }))
      );
      setAttachments((prev) => [...prev, ...next]);
    } catch (err) {
      setError(t('supportSav.fileError'));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.subject.trim() || !form.description.trim()) {
      setError(t('supportSav.validationError'));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        userId: user?.id || user?._id,
        userName: [user?.name, user?.lastName].filter(Boolean).join(' ') || user?.name,
        userEmail: user?.email,
        userPhone: user?.phone,
        providerName: form.providerName?.trim() || undefined,
        requestId: form.requestId?.trim() || undefined,
        requestTitle: form.requestTitle?.trim() || undefined,
        type: form.type,
        subject: form.subject.trim(),
        description: form.description.trim(),
        attachments: attachments.map((a) => a.dataUrl),
        priority: form.priority,
        notifyEmail: form.notifyEmail,
        notifySMS: form.notifySMS,
        rating: form.type === 'evaluation-prestataire' && form.rating ? Number(form.rating) : undefined,
      };

      const res = await api.submitSupportTicket(payload);
      if (res?.success) {
        setSuccess(t('supportSav.submitSuccess'));
        setForm({
          type: 'reclamation',
          subject: '',
          description: '',
          requestId: '',
          requestTitle: '',
          providerName: '',
          priority: 'medium',
          rating: '',
          notifyEmail: true,
          notifySMS: false,
        });
        setAttachments([]);
        await fetchTickets();
        setTab('list');
      } else {
        setError(res?.message || t('supportSav.submitError'));
      }
    } catch (err) {
      setError(err.message || t('supportSav.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  // ----- Detail / Messagerie -----
  const openDetail = async (ticket) => {
    try {
      const res = await api.getSupportTicket(ticket._id);
      if (res?.success) setOpenTicket(res.data);
      else setOpenTicket(ticket);
    } catch {
      setOpenTicket(ticket);
    }
    setMessageDraft('');
    setMessageAttachments([]);
  };

  const closeDetail = () => {
    setOpenTicket(null);
    setMessageDraft('');
    setMessageAttachments([]);
  };

  const handleMsgFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const next = await Promise.all(
        files.slice(0, 3).map(async (f) => ({ name: f.name, dataUrl: await fileToDataUrl(f) }))
      );
      setMessageAttachments((prev) => [...prev, ...next].slice(0, 3));
    } catch {
      // silent
    } finally {
      if (msgFileRef.current) msgFileRef.current.value = '';
    }
  };

  const sendMessage = async () => {
    if (!openTicket?._id || !messageDraft.trim()) return;
    setSendingMessage(true);
    try {
      const res = await api.addSupportTicketMessage(openTicket._id, {
        senderRole: 'client',
        senderName: [user?.name, user?.lastName].filter(Boolean).join(' ') || user?.name,
        senderEmail: user?.email,
        content: messageDraft.trim(),
        attachments: messageAttachments.map((a) => a.dataUrl),
      });
      if (res?.success) {
        setOpenTicket(res.data);
        setMessageDraft('');
        setMessageAttachments([]);
        fetchTickets();
      }
    } catch (e) {
      setError(e.message || t('supportSav.messageError'));
    } finally {
      setSendingMessage(false);
    }
  };

  return (
    <AppLayout title={t('supportSav.pageTitle')} rootClass="layout-1">
      <NioSection className="pt-120 pt-lg-160">
        <NioSection.Head alignX="center">
          <span className="d-inline-block fs-14 text-uppercase text-primary fw-semibold mb-2">{t('supportSav.badge')}</span>
          <h2>{t('supportSav.heading')}</h2>
          <p className="fs-20">{t('supportSav.subtitle')}</p>
        </NioSection.Head>

        <NioSection.Content>
          {error && <div className="alert alert-danger py-2">{error}</div>}
          {success && <div className="alert alert-success py-2">{success}</div>}

          <Row className="gy-3 mb-4">
            <Col md={6} xl={3}>
              <NioCard className="h-100">
                <NioCard.Body className="p-3">
                  <div className="text-muted small">{t('supportSav.statTotal')}</div>
                  <h4 className="mb-0">{stats.total}</h4>
                </NioCard.Body>
              </NioCard>
            </Col>
            <Col md={6} xl={3}>
              <NioCard className="h-100 bg-warning-soft">
                <NioCard.Body className="p-3">
                  <div className="text-muted small">{t('supportSav.statPending')}</div>
                  <h4 className="mb-0">{stats.pending}</h4>
                </NioCard.Body>
              </NioCard>
            </Col>
            <Col md={6} xl={3}>
              <NioCard className="h-100 bg-info-soft">
                <NioCard.Body className="p-3">
                  <div className="text-muted small">{t('supportSav.statInProgress')}</div>
                  <h4 className="mb-0">{stats.inProgress}</h4>
                </NioCard.Body>
              </NioCard>
            </Col>
            <Col md={6} xl={3}>
              <NioCard className="h-100 bg-success-soft">
                <NioCard.Body className="p-3">
                  <div className="text-muted small">{t('supportSav.statResolved')}</div>
                  <h4 className="mb-0">{stats.resolved}</h4>
                </NioCard.Body>
              </NioCard>
            </Col>
          </Row>

          <Tabs activeKey={tab} onSelect={(k) => setTab(k || 'new')} className="mb-4">
            <Tab eventKey="new" title={t('supportSav.tabNew')}>
              <NioCard className="shadow-sm">
                <NioCard.Body className="p-3 p-lg-4">
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('supportSav.requestType')}</Form.Label>
                          <Form.Select
                            value={form.type}
                            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                            disabled={submitting}
                          >
                            {SAV_TYPE_VALUES.map((typeValue) => (
                              <option key={typeValue} value={typeValue}>
                                {typeLabel(typeValue)}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('supportSav.priority')}</Form.Label>
                          <Form.Select
                            value={form.priority}
                            onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                            disabled={submitting}
                          >
                            <option value="low">{t('supportSav.priorities.low')}</option>
                            <option value="medium">{t('supportSav.priorities.medium')}</option>
                            <option value="high">{t('supportSav.priorities.high')}</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('supportSav.subject')}</Form.Label>
                          <Form.Control
                            placeholder={t('supportSav.subjectPlaceholder')}
                            value={form.subject}
                            onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                            disabled={submitting}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('supportSav.reference')}</Form.Label>
                          <Form.Control
                            placeholder={t('supportSav.referencePlaceholder')}
                            value={form.requestTitle}
                            onChange={(e) => setForm((f) => ({ ...f, requestTitle: e.target.value }))}
                            disabled={submitting}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('supportSav.provider')}</Form.Label>
                          <Form.Control
                            placeholder={t('supportSav.providerPlaceholder')}
                            value={form.providerName}
                            onChange={(e) => setForm((f) => ({ ...f, providerName: e.target.value }))}
                            disabled={submitting}
                          />
                        </Form.Group>
                      </Col>
                      {form.type === 'evaluation-prestataire' && (
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t('supportSav.rating')}</Form.Label>
                            <Form.Control
                              type="number"
                              min={1}
                              max={5}
                              step={1}
                              value={form.rating}
                              onChange={(e) => setForm((f) => ({ ...f, rating: e.target.value }))}
                              disabled={submitting}
                            />
                          </Form.Group>
                        </Col>
                      )}
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('supportSav.description')}</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={5}
                            placeholder={t('supportSav.descriptionPlaceholder')}
                            value={form.description}
                            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                            disabled={submitting}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('supportSav.attachments')}</Form.Label>
                          <div className="d-flex gap-2 align-items-center">
                            <Form.Control
                              ref={fileInputRef}
                              type="file"
                              accept="image/*,application/pdf"
                              multiple
                              onChange={handleFileChange}
                              disabled={submitting || attachments.length >= 5}
                            />
                          </div>
                          <Form.Text className="text-muted">{t('supportSav.attachmentsHint')}</Form.Text>
                          {attachments.length > 0 && (
                            <ul className="list-unstyled mt-2 mb-0">
                              {attachments.map((a, idx) => (
                                <li key={idx} className="d-flex align-items-center gap-2 mb-1">
                                  <span className="badge bg-light text-dark border">{a.name}</span>
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm text-danger p-0"
                                    onClick={() => removeAttachment(idx)}
                                    disabled={submitting}
                                  >
                                    {t('supportSav.remove')}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Check
                            type="checkbox"
                            id="notif-email"
                            label={t('supportSav.notifyEmail')}
                            checked={form.notifyEmail}
                            onChange={(e) => setForm((f) => ({ ...f, notifyEmail: e.target.checked }))}
                            disabled={submitting}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Check
                            type="checkbox"
                            id="notif-sms"
                            label={t('supportSav.notifySMS')}
                            checked={form.notifySMS}
                            onChange={(e) => setForm((f) => ({ ...f, notifySMS: e.target.checked }))}
                            disabled={submitting}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex gap-2 mt-2">
                      <NioButton
                        type="submit"
                        label={submitting ? t('supportSav.submitting') : t('supportSav.submit')}
                        className="btn-primary"
                        disabled={submitting}
                      />
                      <NioButton
                        type="button"
                        label={t('supportSav.viewRequests')}
                        className="btn-outline-secondary"
                        onClick={() => setTab('list')}
                        disabled={submitting}
                      />
                    </div>
                  </Form>
                </NioCard.Body>
              </NioCard>
            </Tab>

            <Tab
              eventKey="list"
              title={tickets.length ? t('supportSav.tabListCount', { count: tickets.length }) : t('supportSav.tabList')}
            >
              <NioCard className="shadow-sm">
                <NioCard.Body className="p-3 p-lg-4">
                  <Row className="align-items-center mb-3">
                    <Col md={6}>
                      <h5 className="mb-0">{t('supportSav.historyTitle')}</h5>
                    </Col>
                    <Col md={6}>
                      <Form.Select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="ms-md-auto"
                      >
                        <option value="all">{t('supportSav.filterAll')}</option>
                        <option value="pending">{t('supportSav.statuses.pending')}</option>
                        <option value="in-progress">{t('supportSav.statuses.in-progress')}</option>
                        <option value="resolved">{t('supportSav.statuses.resolved')}</option>
                      </Form.Select>
                    </Col>
                  </Row>

                  {loading ? (
                    <div className="text-muted">{t('supportSav.loading')}</div>
                  ) : !filteredTickets.length ? (
                    <div className="text-muted">{t('supportSav.noTickets')}</div>
                  ) : (
                    <Table responsive bordered hover className="align-middle">
                      <thead>
                        <tr>
                          <th>{t('supportSav.colDate')}</th>
                          <th>{t('supportSav.colType')}</th>
                          <th>{t('supportSav.colSubject')}</th>
                          <th>{t('supportSav.colStatus')}</th>
                          <th>{t('supportSav.colPriority')}</th>
                          <th>{t('supportSav.colMessages')}</th>
                          <th>{t('supportSav.colActions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTickets.map((ticket) => {
                          const st = statusInfo(ticket.status);
                          const pr = priorityInfo(ticket.priority);
                          return (
                            <tr key={ticket._id}>
                              <td>{formatDate(ticket.createdAt)}</td>
                              <td>{typeLabel(ticket.type)}</td>
                              <td>{ticket.subject}</td>
                              <td>
                                <Badge bg={st.variant}>{st.label}</Badge>
                              </td>
                              <td>
                                <Badge bg={pr.variant}>{pr.label}</Badge>
                              </td>
                              <td>{Array.isArray(ticket.messages) ? ticket.messages.length : 0}</td>
                              <td>
                                <NioButton
                                  label={t('supportSav.viewReply')}
                                  className="btn-outline-primary btn-sm"
                                  onClick={() => openDetail(ticket)}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  )}
                </NioCard.Body>
              </NioCard>
            </Tab>
          </Tabs>
        </NioSection.Content>
      </NioSection>

      {/* Modal de detail / messagerie */}
      <Modal show={!!openTicket} onHide={closeDetail} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {openTicket ? `${typeLabel(openTicket.type)} — ${openTicket.subject}` : t('supportSav.modalTicket')}
          </Modal.Title>
        </Modal.Header>
        {openTicket && (
          <Modal.Body>
            <Row className="mb-3">
              <Col md={6}>
                <div className="text-muted small">{t('supportSav.createdAt')}</div>
                <div>{formatDate(openTicket.createdAt)}</div>
              </Col>
              <Col md={6}>
                <div className="text-muted small">{t('supportSav.status')}</div>
                <Badge bg={statusInfo(openTicket.status).variant}>
                  {statusInfo(openTicket.status).label}
                </Badge>
              </Col>
              {openTicket.providerName && (
                <Col md={6} className="mt-3">
                  <div className="text-muted small">{t('supportSav.providerLabel')}</div>
                  <div>{openTicket.providerName}</div>
                </Col>
              )}
              {openTicket.requestTitle && (
                <Col md={6} className="mt-3">
                  <div className="text-muted small">{t('supportSav.referenceLabel')}</div>
                  <div>{openTicket.requestTitle}</div>
                </Col>
              )}
              {openTicket.assignedTo && (
                <Col md={6} className="mt-3">
                  <div className="text-muted small">{t('supportSav.assignedTo')}</div>
                  <div>{openTicket.assignedTo}</div>
                </Col>
              )}
            </Row>

            <div className="mb-3">
              <div className="text-muted small">{t('supportSav.initialDescription')}</div>
              <div className="bg-light p-2 rounded">{openTicket.description}</div>
            </div>

            {Array.isArray(openTicket.attachments) && openTicket.attachments.length > 0 && (
              <div className="mb-3">
                <div className="text-muted small mb-1">{t('supportSav.attachmentsLabel')}</div>
                <div className="d-flex flex-wrap gap-2">
                  {openTicket.attachments.map((a, idx) => (
                    <a key={idx} href={a} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">
                      {t('supportSav.attachmentN', { n: idx + 1 })}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <hr />
            <h6>{t('supportSav.exchanges')}</h6>
            <div className="border rounded p-2 mb-3" style={{ maxHeight: 280, overflowY: 'auto' }}>
              {!openTicket.messages || openTicket.messages.length === 0 ? (
                <div className="text-muted small">{t('supportSav.noMessages')}</div>
              ) : (
                openTicket.messages.map((m, idx) => {
                  const mine = m.senderRole === 'client';
                  return (
                    <div
                      key={idx}
                      className={`mb-2 p-2 rounded ${mine ? 'bg-primary-soft' : 'bg-light'}`}
                    >
                      <div className="small text-muted d-flex justify-content-between">
                        <span>
                          <strong>{m.senderName || m.senderRole}</strong> ({m.senderRole})
                        </span>
                        <span>{formatDate(m.createdAt)}</span>
                      </div>
                      <div>{m.content}</div>
                      {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {m.attachments.map((a, i2) => (
                            <a
                              key={i2}
                              href={a}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-outline-secondary btn-xs btn-sm"
                            >
                              {t('supportSav.attachmentN', { n: i2 + 1 })}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {openTicket.status !== 'resolved' ? (
              <Form>
                <Form.Group className="mb-2">
                  <Form.Label className="small">{t('supportSav.reply')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={messageDraft}
                    onChange={(e) => setMessageDraft(e.target.value)}
                    placeholder={t('supportSav.replyPlaceholder')}
                    disabled={sendingMessage}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="small">{t('supportSav.attachFiles')}</Form.Label>
                  <Form.Control
                    ref={msgFileRef}
                    type="file"
                    accept="image/*,application/pdf"
                    multiple
                    onChange={handleMsgFileChange}
                    disabled={sendingMessage}
                  />
                  {messageAttachments.length > 0 && (
                    <ul className="list-unstyled mt-2 mb-0">
                      {messageAttachments.map((a, idx) => (
                        <li key={idx} className="small">
                          • {a.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </Form.Group>
                <NioButton
                  label={sendingMessage ? t('supportSav.sending') : t('supportSav.send')}
                  className="btn-primary btn-sm"
                  onClick={sendMessage}
                  disabled={sendingMessage || !messageDraft.trim()}
                />
              </Form>
            ) : (
              <div className="alert alert-success py-2 mb-0">{t('supportSav.resolved')}</div>
            )}
          </Modal.Body>
        )}
        <Modal.Footer>
          <NioButton label={t('supportSav.close')} className="btn-outline-secondary" onClick={closeDetail} />
        </Modal.Footer>
      </Modal>
    </AppLayout>
  );
}
